import { useState } from "react";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { OrderedAssemblyItem } from "@/features/book-assembly/book-assembly.types";
import { previewBookAssemblyTocLabel as previewPackageOrderLabel } from "@/lib/book-assembly/manuscript-compiler";

type PackagePreviewStepProps = {
  items: OrderedAssemblyItem[];
  onMove: (itemId: string, direction: "down" | "up") => void;
  onReorder: (activeId: string, targetId: string) => void;
};

export function PackagePreviewStep({
  items,
  onMove,
  onReorder,
}: PackagePreviewStepProps) {
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<string | null>(null);

  return (
    <section className="space-y-6">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {t("bookAssembly.steps.packagePreview.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">
            {t("bookAssembly.steps.packagePreview.title")}
          </h2>
          <p className="max-w-3xl text-sm leading-7 text-muted-foreground">
            {t("bookAssembly.steps.packagePreview.body")}
          </p>
        </div>

        <div className="divide-y divide-ink/10 border-y border-ink/10">
          {items.map((item, index) => {
            const movable = item.sourceKind === "side_story";

            return (
              <div
                className="flex flex-col gap-3 py-5 md:flex-row md:items-center md:justify-between"
                draggable={movable}
                key={`${item.sourceKind}:${item.sourceId}`}
                onDragEnd={() => setDraggingId(null)}
                onDragOver={(event) => {
                  if (!movable) {
                    return;
                  }

                  event.preventDefault();
                }}
                onDragStart={() => setDraggingId(item.sourceId)}
                onDrop={(event) => {
                  event.preventDefault();
                  if (draggingId) {
                    onReorder(draggingId, item.sourceId);
                  }
                  setDraggingId(null);
                }}
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-secondary p-2 text-muted-foreground">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline">{index + 1}</Badge>
                      <Badge variant="secondary">
                        {item.sourceKind === "chapter"
                          ? t("bookAssembly.common.chapter")
                          : t("bookAssembly.common.sideStory")}
                      </Badge>
                      {item.locked ? (
                        <Badge variant="default">{t("bookAssembly.common.locked")}</Badge>
                      ) : null}
                      {item.appendix ? (
                        <Badge variant="outline">{t("bookAssembly.common.appendix")}</Badge>
                      ) : null}
                    </div>
                    <p className="text-lg font-semibold text-foreground">
                      {previewPackageOrderLabel(item)}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    disabled={!movable}
                    onClick={() => onMove(item.sourceId, "up")}
                    type="button"
                    variant="outline"
                  >
                    {t("bookAssembly.steps.packagePreview.moveUp")}
                  </Button>
                  <Button
                    disabled={!movable}
                    onClick={() => onMove(item.sourceId, "down")}
                    type="button"
                    variant="outline"
                  >
                    {t("bookAssembly.steps.packagePreview.moveDown")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
    </section>
  );
}
